import { useEffect, useState } from "react";
import { api, pdfUrl } from "@/lib/api";
import { Plus, Pencil, Trash2, Download, X, FileText } from "lucide-react";
import { toast } from "sonner";

const empty = { number: "", date: "", subject: "", recipient: "", recipient_address: "", body: "", signer_name: "", signer_title: "" };

export default function CopSurat() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/letters").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, date: new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm(it); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const res = editing ? await api.put(`/admin/letters/${editing.id}`, form) : await api.post("/admin/letters", form);
      toast.success(`Surat tersimpan (No. ${res.data.number})`);
      setOpen(false); load();
    } catch { toast.error("Gagal menyimpan"); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Hapus surat ini?")) return; await api.delete(`/admin/letters/${id}`); toast.success("Terhapus"); load(); };

  const inp = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-gold text-sm";

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-syne font-bold text-3xl text-white">Cop Surat</h1><p className="text-white/50 mt-1">Buat surat resmi berkop PT Lotus Lentera & unduh PDF.</p></div>
        <button onClick={openNew} className="btn-gold px-5 py-2.5 rounded-full flex items-center gap-2" data-testid="add-letter"><Plus className="w-4 h-4" /> Buat Surat</button>
      </div>

      <div className="space-y-4">
        {items.map((it) => (
          <div key={it.id} className="bg-ll-card rounded-2xl p-5 gold-border flex items-center justify-between gap-4" data-testid="letter-item">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 rounded-xl btn-gold flex items-center justify-center shrink-0"><FileText className="w-5 h-5" /></div>
              <div className="min-w-0"><div className="text-white font-semibold truncate">{it.subject || "Tanpa Perihal"}</div><div className="text-white/50 text-xs">No. {it.number} · Kepada: {it.recipient}</div></div>
            </div>
            <div className="flex gap-2 shrink-0">
              <a href={pdfUrl(`/admin/letters/${it.id}/pdf`)} target="_blank" rel="noreferrer" className="btn-gold px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm" data-testid="letter-pdf"><Download className="w-4 h-4" /> PDF</a>
              <button onClick={() => openEdit(it)} className="bg-white/5 hover:bg-white/10 text-white rounded-lg px-3 py-2"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(it.id)} className="bg-red-500/10 text-red-400 rounded-lg px-3 py-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/40">Belum ada surat.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#0F1117] rounded-2xl gold-border w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-amber-500/15 sticky top-0 bg-[#0F1117]">
              <h3 className="font-syne font-bold text-white text-lg">{editing ? "Edit" : "Buat"} Surat</h3>
              <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-white/50 uppercase mb-1 block">Nomor (auto jika kosong)</label><input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className={inp} data-testid="letter-number" /></div>
                <div><label className="text-xs text-white/50 uppercase mb-1 block">Tanggal</label><input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inp} /></div>
              </div>
              <div><label className="text-xs text-white/50 uppercase mb-1 block">Perihal</label><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inp} data-testid="letter-subject" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-white/50 uppercase mb-1 block">Kepada</label><input value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} className={inp} /></div>
                <div><label className="text-xs text-white/50 uppercase mb-1 block">Alamat Penerima</label><input value={form.recipient_address} onChange={(e) => setForm({ ...form, recipient_address: e.target.value })} className={inp} /></div>
              </div>
              <div><label className="text-xs text-white/50 uppercase mb-1 block">Isi Surat</label><textarea rows={7} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${inp} resize-none`} data-testid="letter-body" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-white/50 uppercase mb-1 block">Nama Penanda Tangan</label><input value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} className={inp} /></div>
                <div><label className="text-xs text-white/50 uppercase mb-1 block">Jabatan</label><input value={form.signer_title} onChange={(e) => setForm({ ...form, signer_title: e.target.value })} className={inp} /></div>
              </div>
              <button onClick={save} disabled={saving} className="btn-gold w-full py-3 rounded-xl disabled:opacity-60" data-testid="save-letter">{saving ? "Menyimpan…" : "Simpan Surat"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
